'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Status } from '~/components/Notification';
import { useCaptcha } from '~/hooks/useCaptcha';
import { useMarketingOptOut } from '~/hooks/useMarketingOptOut';
import { type ContactFormSchema, contactFormSchema } from '~/schemas/contactFormSchema';
import { trackEmailSubmitted, trackEvent } from '~/utils';
import { getCookie } from '~/utils/cookies';
import { getDisplayCurrencyCookie } from '~/utils/currencyHelpers';
import { isSingleOptInEnabled } from '~/utils/singleOptInFlag';
import { getTrackingParamsFromCookies } from '~/utils/tracking';
type SubmitStatus = {
  state: Status;
  message: string;
} | null;
export const useContactForm = () => {
  const t = useTranslations('content.feedback');
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const showError = (message?: string) => setSubmitStatus({
    state: 'error',
    message: message || t('submit_error')
  });
  const showSuccess = () => setSubmitStatus({
    state: 'success',
    message: t('submit_success')
  });
  const locale = useLocale(); // auto-populated metadata hidden field: format BCP-47 URL locale (en-GB/en-US/…);
  const searchParams = useSearchParams();
  const {
    captchaRef,
    onCaptchaErrored,
    executeCaptcha
  } = useCaptcha();
  const submitMarketingOptOut = useMarketingOptOut('contact_email_capture');
  const singleOptIn = isSingleOptInEnabled();
  const {
    medium: cookieMedium,
    source: cookieSource,
    term: cookieTerm,
    campaign: cookieCampaign,
    campaignId: cookieCampaignId,
    gclid: cookieGclid
  } = getTrackingParamsFromCookies({
    __gtm_campaign_url: getCookie('__gtm_campaign_url')
  });
  const first = (...vals: Array<string | null | undefined>) => {
    for (const v of vals) {
      if (v && `${v}`.trim() && v !== 'null' && v !== 'undefined') return v;
    }
    return undefined;
  };
  const tracking = useMemo(() => {
    const qp = (k: string) => searchParams.get(k);
    return {
      utm_source: first(qp('utm_source'), cookieSource),
      term: first(qp('term'), qp('utm_term'), cookieTerm),
      utm_campaign: first(qp('utm_campaign'), cookieCampaign),
      campaignid: first(qp('campaignid'), qp('campaign_id'), cookieCampaignId),
      gclid: first(qp('gclid'), cookieGclid),
      tripType: first(qp('tripId'), qp('SQF_TRIPID')),
      medium: first(qp('utm_medium'), cookieMedium)
    };
  }, [searchParams, cookieSource, cookieTerm, cookieCampaign, cookieCampaignId, cookieGclid, cookieMedium]);
  const methods = useForm<ContactFormSchema>({
    resolver: zodResolver(contactFormSchema, {
      errorMap: (issue, ctx) => {
        const path = issue.path?.join('.');
        const map: Record<string, string> = {
          firstName: t('name_first_required'),
          lastName: t('name_last_required'),
          message: t('message_required'),
          countryOfResidence: t('country_required'),
          email: issue.code === 'invalid_string' ? t('email_invalid_format') : t('email_required')
        };
        return {
          message: (path && map[path]) ?? ctx.defaultError
        };
      }
    }),
    defaultValues: {
      firstName: '',
      lastName: '',
      message: '',
      email: '',
      captcha: '',
      countryOfResidence: '',
      phone: '',
      whereDidYouHearAboutUs: '',
      medium: '',
      source: '',
      campaign: '',
      campaignid: '',
      gclid: '',
      tripType: '',
      utm_campaign: '',
      utm_source: '',
      middleName: '',
      ...(singleOptIn ? {} : {
        newsletterSignup: false
      }),
      marketingOptOut: false
    }
  });
  const onSubmit = async (data: ContactFormSchema) => {
    setSubmitStatus(null); // clear any prior outcome on resubmit
    // Privacy policy is accepted implicitly by submitting the form (no longer a tick-box).
    const privacyPolicyAccepted = true;
    const newsletterSignup = singleOptIn ? !data.marketingOptOut : data.newsletterSignup;

    // Hidden field, display-currency cookie value (may be undefined → omitted from JSON).
    const currency = getDisplayCurrencyCookie();
    try {
      const captcha = await executeCaptcha();
      const res = await fetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          captcha,
          ...tracking,
          locale,
          currency
        }),
        headers: {
          'Content-type': 'application/json'
        }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => null); // tolerate non-JSON error pages
        showError(errorData?.error);
        return;
      }
    } catch {
      // Network reject / aborted request / captcha failure — surface a generic error.
      showError();
      return;
    } finally {
      // Always re-arm the invisible reCAPTCHA so a retry isn't left hanging.
      captchaRef.current?.reset();
    }
    try {
      // Non-blocking side-effect: 'support lead' already created. A failed opt-out must not strip user's success state (which would invite duplicate resubmit).
      await submitMarketingOptOut({
        ...data,
        privacyPolicyAccepted,
        newsletterSignup
      });
    } catch {
      // submitMarketingOptOut already swallows its own errors,
      // keep success/reset path reachable regardless of shared hook internals.
    }
    trackEmailSubmitted(data.email, 'contact_form', {
      marketingOptOut: data.marketingOptOut
    });
    trackEvent('contact-page-submit', {
      event_category: 'contact_form',
      event_label: 'Contact Form Submitted',
      newsletter_signup: Boolean(newsletterSignup),
      marketing_opt_out: Boolean(data.marketingOptOut)
    });
    showSuccess();
    methods.reset();
  };
  return {
    ...methods,
    Controller,
    onSubmit,
    captchaRef,
    onCaptchaErrored,
    submitStatus
  };
};